<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useRoute } from 'vue-router'


const props = defineProps({
	title: {
		type: String,
		required: true
	},	
	label: {
		type: String,
		required: true
	},
	icon: {
		type: String,
		required: false
	},
	list: {
		type: Array,
		required: true,
		default: () => []
	},
	keyField: {
		type: String,
		required: true,
		default: ''	
	}	
})

const emit = defineEmits(['menuItemSelected'])

//reactive proxies
const isOpen = ref(false)
const focusedIndex = ref(-1)
const dropDownRef = ref(null)
const currentLabel = ref({})

//local variables
const route = useRoute()

//Methods
// const toggleDropdown = () => {
//   isOpen.value = !isOpen.value
// }

const selectOption = (option) => {
	currentLabel.value = option[props.label]
	emit('menuItemSelected', option[props.keyField])
	isOpen.value = false
}


const toggleDropdown = () => {
	isOpen.value = !isOpen.value
 	if (isOpen.value) {
		focusSelectedOrFirst()
	}
}

 const focusSelectedOrFirst = () => {
	//const index = props.list.findIndex(opt => opt[props.keyField] === props.modelValue)
	//focusedIndex.value = index !== -1 ? index : 0
	focusedIndex.value = 0
 }

// Handle outside click
const handleClickOutside = (event) => {
	if (dropDownRef.value && !dropDownRef.value.contains(event.target)) {
		isOpen.value = false
	}
}

const handleKeydown = (e) => {
	if (!isOpen.value || props.list.length === 0) return

	if (e.key === 'ArrowDown') {
		focusedIndex.value = (focusedIndex.value + 1) % props.list.length
		e.preventDefault()
	} else if (e.key === 'ArrowUp') {
		focusedIndex.value = (focusedIndex.value - 1 + props.list.length) % props.list.length
		e.preventDefault()
	} else if (e.key === 'Enter') {
		const option = props.list[focusedIndex.value]
		if (option) selectOption(option)
			e.preventDefault()
	} else if (e.key === 'Escape') {
		isOpen.value = false
		e.preventDefault()
	} else if (e.key === 'Tab') {
		isOpen.value = false
		e.preventDefault()
	}
}

onMounted(() => {
	document.addEventListener('click', handleClickOutside)
  	document.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
	document.removeEventListener('click', handleClickOutside)
  	document.removeEventListener('keydown', handleKeydown)
})

//@keydown.down.prevent="openAndFocus(0)"
//@keydown.enter.prevent="toggleDropdown"

const isActive = computed(() => {
	return route.path.startsWith('/about')
});	
      	
</script>


<template>
<div class="dropdown" ref="dropDownRef">
    <button class="dropdown-button"  :class="{'active':isActive}"
		@click="toggleDropdown"
		@keydown.space.prevent="toggleDropdown"
      	@keydown.esc.prevent="isOpen = false">
      		{{props.title}}
      	<span class="dropdown-icon"><i :class="(isOpen)?'fa-chevron-down':'fa-chevron-right'" class="fa-solid"></i></span>
		<div class="menu-link-underline" :class="{'active':isActive}" />
    </button>

    <ul v-if="isOpen" class="dropdown-menu" role="listbox" aria-label="Dropdown">
		<li v-for="(item, index) in props.list"
			:key="index"
			@click="selectOption(item)"
			@mouseenter="focusedIndex = index"
			class="dropdown-item"
			:class="{ focused: index === focusedIndex }"
			role="option"
			:aria-selected="currentLabel ===  item[props.label]"
        	v-html="item[props.label] + (item[props.icon] ? ' ' + item[props.icon] : '')" >
      	</li>
    </ul>
</div>
</template>
   
<style scoped>
.menu-link-underline {
	background: var(--border-color-menu-DividerActive, #C8A52D); 
	height: .25rem; 
	width: 100%; 
	margin-top: 0.5rem;
	visibility: hidden;
}

.menu-link-underline.active {
  display: block;
  visibility:visible;
}

.dropdown {
	position: relative;
	display: inline-block;
}

.dropdown-button {
	text-decoration: none;
	outline:none;
	width: 100%;
	min-width:unset;	
	padding: 0;
	height: 100%;

	max-height: 2.1875rem; /* 2.25rem; */
	min-height: 1.5rem;
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base);
	font-weight: 500;
	background-color:transparent;
	color: var(--text-Primary);
    display: inline-block;
    text-align: start;	
	border: none;
	border-radius: 0;
	cursor: pointer;
	text-wrap: nowrap;
}

.dropdown-button:active, .dropdown-button:focus {
	border: 0;
	color: var(--text-Link, #064291);	
    outline-color: var(--border-color-input-Active);
    outline-width: 2px;
    outline-style: solid;
    outline-offset: 1rem;	
	ZZborder-radius: 0;
	text-decoration: none;
}

 .dropdown-button:focus:not(:focus-visible) {
	outline: 0;
 }

.dropdown-button:hover {
	text-decoration: none;
		color: var(--text-Link, #064291);
}

.dropdown-icon {
	font-size: 12px;
	margin-left: 0px;
}

.dropdown-menu {
	position: absolute;
	top: 1.5rem;
	right: 0;
	border-radius: 0.25rem;
	border: 1px solid var(--border-color-input-Border, #A9B7C7);	
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
	z-index: 1000;
	list-style: none;
	padding: 0;
    text-wrap: nowrap;
	text-align: left;
    max-width: 500px;	
	background-color: var(--surface-color-Tertiary, #FAFAFA);
	overflow: hidden;
	text-overflow: ellipsis;
}

.dropdown-item {
	color: var(--text-Primary);
	cursor: pointer;
  	height: 2.5rem;
	padding: 0.5rem 0.75rem;
	border-bottom: 1px solid var(--border-color-input-Border);
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 1rem);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-Base, 1.5rem);
	background: var(--surfaces-surface-Default, #FFF);
	min-width: 200px;
}


.dropdown-item:hover, .dropdown-item:focus, .dropdown-item.focused {
	background-color: var(--input-background-Hover, #FAFAFA);
	Zborder-top: 1.5px solid var(--border-divider-Active, #064291);	
	Zborder-bottom: 1.5px solid var(--border-divider-Active, #064291);	
	color: var(--text-Link, #064291);
}

.dropdown-100-width {
	width: 100%
}

button:focus {
	outline: 0;
}

/* max size 1023px - iPad */
@media (max-width: 63.9375rem) {
	.dropdown {
		width: 100%;
	}

	.dropdown-button .menu-link-underline {
		display:none;
	}

	.dropdown-button {
		line-height: var(--font-line-height-H4, 1.5rem); /* 150% */	
	
		font-size: var(--font-size-H4);	
		max-height: unset;

		padding: var(--spacing-xl);
		border-bottom: 1px solid var(--border-color-input-Border);
		width: 100%;
		height:77px;
	}

	.dropdown-button:focus {
		outline: none;
		background-color: var(--input-background-Hover, #FAFAFA);	
	}

	.dropdown-button.active {
		border-bottom: var(--border-color-menu-DividerActive, #C8A52D) solid .25rem;
	}	

	.dropdown-menu {
		position: relative;
		top: 0;
		right: 0;
		max-width: unset;
		width: 23.4375rem;
		background-color: var(--surface-color-Default);
        padding: 0;		
		margin: 0;
		border: 1px solid var(--border-color-input-Border, #A9B7C7);
		border-right-width:2px;
		ZZborder-top-width:0;

	}

	
	.dropdown-button.active .dropdown-menu {
		top: .5rem;
	}
	

	.dropdown-item {
		height: unset;
		padding: var(--spacing-xl) var(--spacing-xl) var(--spacing-xl) 3.5rem;

		width: 100%;
		color: var(--text-Primary, #2C2C2C);

		font-size: var(--font-size-H4, 1rem);
		font-style: normal;
		font-weight: 500;	
	}

 	.dropdown-item:hover, .dropdown-item:focus {
		background-color: var(--input-background-Hover, #FAFAFA);
	}

	.fa-xs {
		font-size: unset;
		line-height: unset; 
		vertical-align: unset;
	}	
}

/* 767px - mobile */
@media (max-width: 47.9375rem) {

	.dropdown {
		width: 100%;
	}

	/*
	.dropdown-button {
		line-height: var(--font-line-height-H4, 1.5rem); 	
		height: var(--font-line-height-H4, 1.5rem);		
	}
	*/

	.dropdown-menu {
		position: relative;
		top: 0;
		margin-top: 0;
		width: 100%;
		border: 0;
		border-top: 1px solid var(--border-color-input-Border);
		background-color: var(--surface-color-Default);	
	}

	/*
	.menu-item.active .dropdown-menu {
		top: 1.75rem;
	}
	*/

	.dropdown-item {
		height: unset;	
		padding: var(--spacing-xl) var(--spacing-xl) var(--spacing-xl) 3.5rem;
		width: 100%;
		color: var(--text-Primary, #2C2C2C);

		font-size: var(--font-size-H4, 1rem);
		font-style: normal;
		font-weight: 500;	
	}

 	.dropdown-item:hover, .dropdown-item:focus {
		background-color: var(--input-background-Hover, #FAFAFA);
	}	

	.dropdown-button .menu-link-underline {
		visibility: hidden;
	}

	.fa-xs {
		font-size: unset;
		line-height: unset; 
		vertical-align: unset;
	}
}

</style>
